import { useQueries } from "@tanstack/react-query";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { useEffect, useMemo, useState } from "react";
import { classifyStagedAttachment } from "../../../api/routes";
import * as client from "../../../client";
import { useClientId } from "../../../ClientConfig";
import { useToast } from "../../../context-providers/Toast";
import {
  iconForFilename,
  validFileTypesForFilename,
} from "../attachments/AttachmentCard";
import { attachmentsUiTypeMap } from "../attachments/mapping";
import { useExtractStagedAttachments } from "../data";
import styles from "./EmailAttachmentStagingDialog.module.css";
import { formatFileSize } from "./EmailThreadModal";

type AttachmentType = client.Attachment["original_mapping"];

// The staging id is 32 hex chars, so ":" can't collide with it.
const rowKeyOf = (stagingId: string, name: string) => `${stagingId}:${name}`;

/** Per-row user edits; everything else is derived from the manifest and the
 * classification queries. */
interface RowEdit {
  include?: boolean;
  manual?: AttachmentType;
}

interface EmailAttachmentStagingDialogProps {
  reportId: string;
  manifests: client.StagedEmailManifest[];
  /** Classifications that trigger a report reprocess, as reported by the
   * upload response — the backend owns this mapping. */
  reprocessTypes: AttachmentType[];
  visible: boolean;
  onHide: () => void;
}

/**
 * Review tray shown after emails are dropped on a report: lists the
 * attachments inside each email with an auto-classification suggestion per
 * row, and promotes the selected ones to the report's attachments.
 */
export const EmailAttachmentStagingDialog: React.FC<
  EmailAttachmentStagingDialogProps
> = ({ reportId, manifests, reprocessTypes, visible, onHide }) => {
  const clientId = useClientId();
  const toast = useToast();
  const extractMutation = useExtractStagedAttachments(reportId);

  const rows = useMemo(
    () =>
      manifests.flatMap((manifest) =>
        manifest.attachments.map((attachment) => ({
          key: rowKeyOf(manifest.staging_id, attachment.name),
          manifest,
          attachment,
        }))
      ),
    [manifests]
  );

  const [edits, setEdits] = useState<Record<string, RowEdit>>({});
  useEffect(() => {
    setEdits({});
  }, [manifests]);

  // One classification call per attachment so cheap rows resolve immediately
  // and a slow OCR-bound PDF only holds up its own row.
  const classificationQueries = useQueries({
    queries: rows.map((row) => ({
      queryKey: [
        "staged-classification",
        reportId,
        row.manifest.staging_id,
        row.attachment.name,
      ],
      queryFn: () =>
        classifyStagedAttachment(
          reportId,
          row.manifest.staging_id,
          row.attachment.name
        ),
      enabled: visible,
      staleTime: Infinity,
      retry: 1,
    })),
  });

  const viewRows = rows.map((row, index) => {
    const query = classificationQueries[index];
    const edit = edits[row.key];
    const suggested = query?.data?.classification ?? null;
    const manual = edit?.manual ?? null;
    return {
      ...row,
      suggested,
      manual,
      effective: manual ?? suggested,
      include: edit?.include ?? true,
      classifying: Boolean(query?.isPending),
      classifyFailed: Boolean(query?.isError),
    };
  });
  const rowsByStagingId = new Map<string, typeof viewRows>();
  for (const row of viewRows) {
    const group = rowsByStagingId.get(row.manifest.staging_id) ?? [];
    group.push(row);
    rowsByStagingId.set(row.manifest.staging_id, group);
  }

  const includedRows = viewRows.filter((row) => row.include);
  const pendingIncluded = includedRows.some((row) => row.effective === null);
  const reprocessSet = useMemo(() => new Set(reprocessTypes), [reprocessTypes]);
  const anyReprocess = includedRows.some(
    (row) => row.effective !== null && reprocessSet.has(row.effective)
  );

  const setEdit = (key: string, update: RowEdit) => {
    setEdits((prev) => ({ ...prev, [key]: { ...prev[key], ...update } }));
  };

  const handleAdd = () => {
    const selections: client.StagedAttachmentSelection[] = includedRows.flatMap(
      (row) => {
        if (row.effective === null) return [];
        return [
          {
            staging_id: row.manifest.staging_id,
            attachment_name: row.attachment.name,
            // The machine suggestion is the original mapping. The user's pick
            // is a manual override whenever it differs from the suggestion —
            // including when classification failed and there is no suggestion
            // — so it survives report reruns.
            original_classification: row.suggested ?? row.effective,
            manual_classification:
              row.manual !== null && row.manual !== row.suggested
                ? row.manual
                : null,
            conversation_id: row.manifest.conversation_id ?? null,
          },
        ];
      }
    );

    extractMutation.mutate(selections, {
      onSuccess: (result) => {
        const rerun = result.rerun_enqueued
          ? " The report is reprocessing."
          : "";
        toast.show({
          severity: "success",
          summary: "Attachments added",
          detail: `Added ${result.added.length} file(s) to the Attachments tab.${rerun}`,
          life: 5000,
        });
        onHide();
      },
      onError: () => {
        toast.show({
          severity: "error",
          summary: "Extraction failed",
          detail: "Could not add attachments. Please try again.",
          life: 5000,
        });
      },
    });
  };

  return (
    <Dialog
      header="Add attachments from dropped emails?"
      visible={visible}
      onHide={onHide}
      dismissableMask={false}
      className={styles.dialog}
    >
      <div className={styles.body}>
        <p className={styles.hint}>
          The emails were linked to this report. Select which of their
          attachments to add to the Attachments tab, and adjust the suggested
          classification if needed.
        </p>
        {manifests.map((manifest) => (
          <div key={manifest.staging_id} className={styles.emailGroup}>
            <div className={styles.emailHeader}>
              <i className={`pi pi-envelope ${styles.emailHeaderIcon}`} />
              <span>{manifest.subject ?? manifest.filename}</span>
            </div>
            {(rowsByStagingId.get(manifest.staging_id) ?? []).map((row) => {
              const options = Array.from(
                new Set([
                  ...validFileTypesForFilename(row.attachment.name, clientId),
                  ...(row.suggested ? [row.suggested] : []),
                ])
              ).map((type) => ({
                label: attachmentsUiTypeMap[type],
                value: type,
              }));
              return (
                <div
                  key={row.key}
                  className={`${styles.row} ${row.include ? "" : styles.rowExcluded}`}
                >
                  <Checkbox
                    checked={row.include}
                    onChange={(e) =>
                      setEdit(row.key, { include: Boolean(e.checked) })
                    }
                  />
                  <i
                    className={`${iconForFilename(row.attachment.name)} ${styles.fileIcon}`}
                  />
                  <span className={styles.fileName} title={row.attachment.name}>
                    {row.attachment.name}
                  </span>
                  {row.classifyFailed && row.manual === null && (
                    <Tag
                      severity="danger"
                      value="Auto-classify failed — pick a type"
                      className={styles.rowTag}
                    />
                  )}
                  <span className={styles.fileSize}>
                    {formatFileSize(row.attachment.size)}
                  </span>
                  {row.effective !== null &&
                    reprocessSet.has(row.effective) && (
                      <Tag
                        severity="info"
                        value="Reprocesses report"
                        className={styles.rowTag}
                      />
                    )}
                  {row.classifying ? (
                    <span className={styles.classifying}>
                      <ProgressSpinner
                        className={styles.rowSpinner}
                        strokeWidth="6"
                      />
                      Classifying...
                    </span>
                  ) : (
                    <Dropdown
                      className={styles.classification}
                      value={row.effective}
                      options={options}
                      placeholder="Select type"
                      disabled={!row.include}
                      onChange={(e) =>
                        setEdit(row.key, { manual: e.value as AttachmentType })
                      }
                    />
                  )}
                </div>
              );
            })}
          </div>
        ))}
        <div className={styles.footer}>
          <span className={styles.footerNote}>
            {anyReprocess
              ? "Adding SOVs, loss runs, or policy documents re-processes the report."
              : ""}
          </span>
          <div className={styles.footerButtons}>
            <Button
              label="Skip"
              severity="secondary"
              text
              disabled={extractMutation.isPending}
              onClick={onHide}
            />
            <Button
              label={`Add ${includedRows.length} attachment${includedRows.length === 1 ? "" : "s"}`}
              disabled={
                includedRows.length === 0 ||
                pendingIncluded ||
                extractMutation.isPending
              }
              loading={extractMutation.isPending}
              onClick={handleAdd}
            />
          </div>
        </div>
      </div>
    </Dialog>
  );
};
